<script setup lang="ts">
import dayjs, { type Dayjs } from 'dayjs'
import { computed, ref } from 'vue'
import { $t } from '../../../../../../composables/i18n'
import PSpace from '../../../../../space/PSpace.vue'
import { PSpaceSize } from '../../../../../space/types'
import { PTimeInputFieldIncrement } from '../../../../types'
import DrumColumn from './components/DrumColumn.vue'

type Props = {
  increment: PTimeInputFieldIncrement
}

const props = defineProps<Props>()
const model = defineModel<Dayjs | null>()

const availableHours: string[] = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))

const availableMinutes = computed<string[]>(() => {
  const steps = Math.floor(60 / props.increment)
  return Array.from({ length: steps }, (_, i) => (i * props.increment).toString().padStart(2, '0'))
})

const showMinutes = computed(() => availableMinutes.value.length > 1)

const resolveInitialIndices = (): { hourIndex: number; minuteIndex: number } => {
  const value = model.value

  if (!value) {
    return { hourIndex: 0, minuteIndex: 0 }
  }

  const hourStr = value.tz().hour().toString().padStart(2, '0')
  const minuteStr = value.tz().minute().toString().padStart(2, '0')
  const hourIndex = availableHours.indexOf(hourStr)
  const minuteIndex = availableMinutes.value.indexOf(minuteStr)

  return {
    hourIndex: hourIndex >= 0 ? hourIndex : 0,
    minuteIndex: minuteIndex >= 0 ? minuteIndex : 0,
  }
}

const initial = resolveInitialIndices()
const hourIndex = ref<number>(initial.hourIndex)
const minuteIndex = ref<number>(initial.minuteIndex)

const updateModel = (hour: number, minute: number): void => {
  model.value = (model.value ?? dayjs()).tz().hour(hour).minute(minute)
}

const onHourSnap = (newIndex: number): void => {
  hourIndex.value = newIndex
  updateModel(
    parseInt(availableHours[newIndex] ?? '0'),
    parseInt(availableMinutes.value[minuteIndex.value] ?? '0'),
  )
}

const onMinuteSnap = (newIndex: number): void => {
  minuteIndex.value = newIndex
  updateModel(
    parseInt(availableHours[hourIndex.value] ?? '0'),
    parseInt(availableMinutes.value[newIndex] ?? '0'),
  )
}
</script>

<template>
  <div class="ds-drum-content" role="group" :aria-label="$t('Select time')">
    <p-space :mobile="PSpaceSize.XL" vertical />
    <div class="ds-drum-content__drums">
      <p-space :mobile="PSpaceSize.LG" />
      <drum-column
        :items="availableHours"
        :selected-index="hourIndex"
        @update:selected-index="onHourSnap"
      />
      <template v-if="showMinutes">
        <p-space :mobile="PSpaceSize.MD" />
        <span class="ds-drum-content__separator" aria-hidden="true"> </span>
        <p-space :mobile="PSpaceSize.MD" />
        <drum-column
          :items="availableMinutes"
          :selected-index="minuteIndex"
          @update:selected-index="onMinuteSnap"
        />
      </template>
      <p-space :mobile="PSpaceSize.LG" />
    </div>
    <p-space :mobile="PSpaceSize.XL" vertical />
  </div>
</template>

<style scoped lang="scss">
@import '../../../../../../assets/scss/shared/_brand.module.scss';

.ds-drum-content {
  &__drums {
    display: flex;
    align-items: flex-start;
    justify-content: center;
  }

  &__separator {
    position: relative;
    width: 10px;
    height: 40px;
    align-self: center;
  }

  &__separator::before,
  &__separator::after {
    content: '';
    position: absolute;
    left: 50%;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: getColor('grey300');
    transform: translate(-50%, -50%);
  }

  &__separator::before {
    top: calc(50% - 6px);
  }

  &__separator::after {
    top: calc(50% + 6px);
  }
}
</style>
